import React, { useEffect, useState } from "react";
import { Eye, X } from "lucide-react";
import { useBodyScrollLock } from "../../../hooks/useBodyScrollLock";
import axios from "axios";
// import rental_img_1 from "../../../Assets/Images/Rental/rental_img (1).png";
// import rental_img_2 from "../../../Assets/Images/Rental/rental_img (2).png";
// import rental_img_3 from "../../../Assets/Images/Rental/rental_img (3).png";
// import rental_img_4 from "../../../Assets/Images/Rental/rental_img (4).png";
// import rental_img_5 from "../../../Assets/Images/Rental/rental_img (5).png";
// import rental_img_6 from "../../../Assets/Images/Rental/rental_img (6).png";
// import rental_img_7 from "../../../Assets/Images/Rental/rental_img (7).png";
// import rental_img_8 from "../../../Assets/Images/Rental/rental_img (8).png";

const FacilitySkeleton = () => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-2 gap-4 md:gap-6">
      {Array.from({ length: 4 }).map((_, idx) => (
        <div
          key={idx}
          className="animate-pulse bg-gray-500 rounded-lg shadow-lg overflow-hidden"
        >
          <div className="aspect-[5/3] bg-gray-400"></div>
          <div className="p-4 flex justify-between items-center">
            <div className="h-4 w-24 bg-gray-300 rounded"></div>
            <div className="h-5 w-16 bg-gray-300 rounded"></div>
          </div>
        </div>
      ))}
    </div>
  );
};

const FacilityImageGallery = () => {
  const [selectedImage, setSelectedImage] = useState(null);

  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const fetchData = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(
        `${process.env.REACT_APP_API_URL}/fields-rental/all-fields`
      );
      if (data?.rentals) {
        setFields(data.rentals);
      }
      // console.log("data", data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useBodyScrollLock(selectedImage);

  // Handle ESC key press to close lightbox
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === "Escape" && selectedImage) {
        closeLightbox();
      }
    };

    document.addEventListener("keydown", handleEscapeKey);

    // Cleanup event listener on unmount or when selectedImage changes
    return () => {
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [selectedImage]);

  const openLightbox = (field) => {
    setSelectedImage(field);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  return (
    <div className="w-full px-4 md:px-8 lg:px-12">
      {/* Image Gallery Grid */}
      <div className="w-full mx-auto">
        {loading ? (
          <FacilitySkeleton />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-2 gap-4 md:gap-6">
            {fields?.map((field) => (
              <div
                key={field._id}
                className="group relative overflow-hidden rounded-lg  shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-1 bg-black"
              >
                {/* Image Container */}
                <div className="relative aspect-[5/3] overflow-hidden">
                  <img
                    src={field.imageUrl}
                    alt={field.fieldName}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 opacity-90"
                  />

                  {/* Dark overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />

                  <div className="flex flex-col md:flex-row gap-y-2 absolute bottom-4 md:justify-between left-4 right-4 z-10">
                    {/* Facility Name - Bottom Left */}
                    <div className="">
                      <h3 className="text-white font-semibold font-heading text-base sm:text-lg tracking-wide uppercase leading-tight">
                        {field.fieldName}
                      </h3>
                    </div>
                    {/* Badge - Bot Right */}
                    {field?.price && (
                      <>
                        <div className="sm:self-end">
                          <span className="px-3 py-1 text-[#FF4141] bg-white text-xs sm:text-sm font-heading font-semibold rounded-md leading-relaxed shadow-lg tracking-wider">
                            ${field.price}/{field.time.toUpperCase()}
                          </span>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Hover Overlay with View Button */}
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                    <button
                      onClick={() => openLightbox(field)}
                      className="bg-white/20 backdrop-blur-sm p-4 rounded-full  outline-none hover:bg-white/30 transition-all duration-200 transform hover:scale-110"
                    >
                      <Eye className="w-6 h-6 text-white" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4"
          onClick={closeLightbox}
        >
          <div className="relative max-w-5xl w-full">
            {/* ESC hint */}
            <div className="absolute hidden sm:block     top-4 left-4 z-10  bg-black/50 backdrop-blur-sm px-2 sm:px-3 py-1 rounded-full">
              <span className="text-white text-xs sm:text-sm font-medium">
                Press ESC to close
              </span>
            </div>
            {/* Close Button */}
            <button
              onClick={closeLightbox}
              className="absolute top-4 right-4 z-10 w-10 h-10 bg-black/50 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-black/70 transition-all duration-200"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Large Image */}
            <div className="relative aspect-[16/10] rounded-2xl overflow-hidden">
              <img
                src={selectedImage.imageUrl}
                alt={selectedImage.fieldName}
                className="w-full h-full object-cover"
              />

              {/* Image Overlay with Title */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
              <div className="flex flex-col md:flex-row gap-y-2 absolute bottom-4 md:justify-between left-4 right-4 z-10">
                {/* Facility Name - Bottom Left */}
                <div className="">
                  <h3 className="text-white font-semibold font-heading text-base sm:text-lg tracking-wide uppercase leading-tight">
                    {selectedImage.name}
                  </h3>
                </div>
                {/* Badge - Bot Right */}
                {selectedImage?.price && (
                  <>
                    <div className="sm:self-end">
                      <span className="px-3 py-1 text-[#FF4141] bg-white text-xs sm:text-sm font-heading font-semibold rounded-md leading-relaxed shadow-lg tracking-wider">
                        ${selectedImage.price}/
                        {selectedImage.time.toUpperCase()}
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FacilityImageGallery;
